import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth, useTheme } from '../App';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Crown, 
  Check, 
  Moon, 
  Sun,
  CreditCard,
  Loader2,
  Settings as SettingsIcon,
  Trash2,
  AlertTriangle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Settings() {
  const { user, refreshUser, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const checkPaymentStatus = async (sessionId) => {
    setCheckingPayment(true);
    let attempts = 0;
    const maxAttempts = 5;
    
    const poll = async () => {
      try {
        const response = await axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: true });
        
        if (response.data.payment_status === 'paid') {
          toast.success('Payment successful! Welcome to Pro!');
          refreshUser();
          // Clear session_id from URL
          window.history.replaceState({}, '', '/settings');
          return;
        }
        
        if (response.data.status === 'expired') {
          toast.error('Payment session expired');
          return;
        }
        
        if (attempts < maxAttempts) {
          attempts++;
          setTimeout(poll, 2000);
        } else {
          toast.error('Payment verification timed out');
        }
      } catch (error) {
        console.error('Error checking payment:', error);
        toast.error('Failed to verify payment');
      } finally {
        if (attempts >= maxAttempts) {
          setCheckingPayment(false);
        }
      }
    };
    
    poll();
  };

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/payments/checkout`, {
        plan_id: 'pro_monthly',
        origin_url: window.location.origin
      }, { withCredentials: true });
      
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Failed to start checkout');
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/portal`, {
        return_url: window.location.origin + '/settings'
      }, { withCredentials: true });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not open billing portal');
      setPortalLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'DELETE') {
      toast.error('Type DELETE to confirm');
      return;
    }
    setDeleting(true);
    try {
      await axios.delete(`${API}/auth/account`, { withCredentials: true });
      toast.success('Account deleted');
      setTimeout(() => logout(), 500);
    } catch (e) {
      toast.error('Failed to delete account');
      setDeleting(false);
    }
  };

  const isPro = user?.subscription_tier === 'pro';

  return (
    <div className="container mx-auto px-4 md:px-8 py-8" data-testid="settings-page">
      <h1 className="font-heading text-3xl mb-8">SETTINGS</h1>

      {/* Payment Status Check */}
      {checkingPayment && (
        <div className="border border-primary p-4 mb-8 flex items-center gap-3" data-testid="payment-checking">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Verifying your payment...</span>
        </div>
      )}

      {/* Subscription Section */}
      <section className="border border-border mb-8" data-testid="subscription-section">
        <div className="p-6 border-b border-border">
          <h2 className="font-heading text-lg flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            SUBSCRIPTION
          </h2>
        </div>
        
        <div className="p-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Free Plan */}
            <div className={`border p-6 ${!isPro ? 'border-primary' : 'border-border'}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading">FREE</h3>
                {!isPro && <span className="text-xs uppercase text-primary">CURRENT</span>}
              </div>
              <p className="text-3xl font-heading mb-4">$0<span className="text-sm text-muted-foreground">/mo</span></p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-accent" />
                  2 autofills per month
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-accent" />
                  Basic document storage
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-accent" />
                  Standard templates
                </li>
              </ul>
            </div>

            {/* Pro Plan */}
            <div className={`border p-6 ${isPro ? 'border-accent' : 'border-border'} relative`}>
              {isPro && (
                <div className="absolute -top-3 left-4 px-2 bg-accent text-accent-foreground text-xs uppercase">
                  ACTIVE
                </div>
              )}
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading flex items-center gap-2">
                  <Crown className="w-5 h-5 text-yellow-500" />
                  PRO
                </h3>
              </div>
              <p className="text-3xl font-heading mb-4">$9.99<span className="text-sm text-muted-foreground">/mo</span></p>
              <ul className="space-y-2 text-sm mb-6">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-accent" />
                  <strong>Unlimited</strong> autofills
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-accent" />
                  Advanced AI parsing
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-accent" />
                  Priority processing
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-accent" />
                  Application packs
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-accent" />
                  Success optimization
                </li>
              </ul>
              {!isPro && (
                <Button 
                  onClick={handleUpgrade}
                  disabled={loading}
                  className="w-full brutalist-shadow uppercase text-xs tracking-wider"
                  data-testid="upgrade-btn"
                >
                  {loading ? 'LOADING...' : 'UPGRADE TO PRO'}
                </Button>
              )}
              {isPro && (
                <Button
                  variant="outline"
                  onClick={handleManageSubscription}
                  disabled={portalLoading}
                  className="w-full uppercase text-xs tracking-wider"
                  data-testid="manage-subscription-btn"
                >
                  <SettingsIcon className="w-4 h-4 mr-2" />
                  {portalLoading ? 'OPENING...' : 'MANAGE / CANCEL'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Appearance Section */}
      <section className="border border-border" data-testid="appearance-section">
        <div className="p-6 border-b border-border">
          <h2 className="font-heading text-lg flex items-center gap-2">
            {theme === 'dark' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            APPEARANCE
          </h2>
        </div>
        
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">DARK MODE</Label>
              <p className="text-xs text-muted-foreground mt-1">Toggle between light and dark themes</p>
            </div>
            <Switch
              checked={theme === 'dark'}
              onCheckedChange={toggleTheme}
              data-testid="theme-switch"
            />
          </div>
        </div>
      </section>

      {/* Account Info */}
      <section className="border border-border mt-8" data-testid="account-section">
        <div className="p-6 border-b border-border">
          <h2 className="font-heading text-lg">ACCOUNT</h2>
        </div>
        
        <div className="p-6 space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground uppercase">Name</span>
            <span className="font-mono">{user?.name}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground uppercase">Email</span>
            <span className="font-mono">{user?.email}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground uppercase">Plan</span>
            <span className={`font-mono uppercase ${isPro ? 'text-accent' : ''}`}>{user?.subscription_tier}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground uppercase">Autofills Used</span>
            <span className="font-mono">{user?.autofills_used}/{isPro ? '∞' : user?.autofills_limit}</span>
          </div>
        </div>
      </section>

      {/* Danger Zone */}
      <section className="border border-destructive/40 mt-8" data-testid="danger-zone">
        <div className="p-6 border-b border-destructive/40">
          <h2 className="font-heading text-lg flex items-center gap-2 text-destructive">
            <AlertTriangle className="w-5 h-5" />
            DANGER ZONE
          </h2>
        </div>
        <div className="p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <p className="font-medium text-sm mb-1">Delete account</p>
            <p className="text-xs text-muted-foreground">
              Permanently removes your profile, documents, applications, and cancels any active subscription. Cannot be undone.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => setDeleteDialog(true)}
            className="border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground uppercase text-xs tracking-wider"
            data-testid="open-delete-dialog-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" /> DELETE ACCOUNT
          </Button>
        </div>
      </section>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onOpenChange={(open) => { setDeleteDialog(open); if (!open) setDeleteConfirm(''); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-wider text-destructive flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              DELETE ACCOUNT
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-muted-foreground">
              This will permanently delete your profile, all uploaded documents, all applications, and cancel any active subscription. You will not be able to recover this data.
            </p>
            <div>
              <Label className="text-xs uppercase tracking-widest mb-2 block">Type <span className="font-mono text-destructive">DELETE</span> to confirm</Label>
              <Input
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder="DELETE"
                className="font-mono"
                data-testid="delete-confirm-input"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                variant="destructive"
                onClick={handleDeleteAccount}
                disabled={deleteConfirm !== 'DELETE' || deleting}
                className="flex-1 uppercase text-xs tracking-wider"
                data-testid="confirm-delete-btn"
              >
                {deleting ? 'DELETING...' : 'PERMANENTLY DELETE'}
              </Button>
              <Button variant="outline" onClick={() => setDeleteDialog(false)} className="uppercase text-xs tracking-wider">
                CANCEL
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
